import { useMemo, useState } from 'react';
import { Plus, ReceiptText, Tags, RefreshCw, FileText, Building2, Check } from 'lucide-react';
import { Money } from '@/shared/display/Money';
import { DateDisplay } from '@/shared/display/DateDisplay';
import { StatCard, PageHeader } from '@/cajita/ui';
import { CxDialog } from '@/cajita/overlay';
import { useAccounts } from '@/features/treasury/treasury.api';
import { useExpenses, useCreateExpense, useExpenseCategories, useCreateCategory } from './expenses.api';

const todayISO = () => new Date().toISOString().slice(0, 10);
const fmt = (n: number | string): string =>
  'S/ ' + Number(n).toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const CAT_PALETTE = ['#3B82F6', '#8B5CF6', '#F59E0B', '#EC4899', '#22C55E', '#0EA5E9', '#EF4444', '#64748B'];
const catColor = (name: string): string => CAT_PALETTE[[...name].reduce((a, c) => a + c.charCodeAt(0), 0) % CAT_PALETTE.length]!;

export function ExpensesPage(): JSX.Element {
  const { data, isLoading } = useExpenses();
  const [dialog, setDialog] = useState<null | 'expense' | 'category'>(null);
  const items = data?.items ?? [];

  const kpis = useMemo(() => {
    const totalMes = items.reduce((s, e) => s + Number(e.total), 0);
    const igvTotal = items.reduce((s, e) => s + Number(e.igv ?? 0), 0);
    const byCat = new Map<string, number>();
    for (const e of items) if (e.category) byCat.set(e.category, (byCat.get(e.category) ?? 0) + Number(e.total));
    let topCat = '—';
    let topVal = 0;
    for (const [k, v] of byCat) if (v > topVal) { topCat = k; topVal = v; }
    return { totalMes, igvTotal, topCat, topVal, count: items.length };
  }, [items]);

  return (
    <div className="cx cx-main" style={{ minHeight: '100%', padding: '28px 32px 64px' }}>
      <div className="page fade-in">
        <PageHeader
          title="Gastos"
          sub="Registro y categorización de egresos"
          actions={
            <>
              <button className="btn btn-secondary" onClick={() => setDialog('category')}>
                <Tags size={17} />
                Categorías
              </button>
              <button className="btn btn-primary" onClick={() => setDialog('expense')}>
                <Plus size={17} />
                Nuevo gasto
              </button>
            </>
          }
        />

        <div className="kpi-grid" style={{ marginBottom: 20 }}>
          <StatCard label="Gastos (cargados)" value={fmt(kpis.totalMes)} icon={<ReceiptText size={19} />} accent="#EF4444" />
          <StatCard label="IGV recuperable" value={fmt(kpis.igvTotal)} icon={<RefreshCw size={19} />} accent="#22C55E" />
          <StatCard label="Mayor categoría" value={kpis.topCat} icon={<Building2 size={19} />} accent="#F59E0B" sub={kpis.topVal ? fmt(kpis.topVal) : undefined} />
          <StatCard label="Registros" value={String(kpis.count)} icon={<FileText size={19} />} accent="#3B82F6" />
        </div>

        <div className="card">
          <div className="card-head">
            <div className="card-title">Detalle de gastos</div>
            <span className="t-sm muted">{items.length} registros</span>
          </div>
          {isLoading ? (
            <div style={{ padding: '8px 0' }}>
              {Array.from({ length: 6 }).map((_, r) => (
                <div key={r} className="row gap16" style={{ padding: '13px 16px', borderBottom: '1px solid #F1F5F9' }}>
                  {Array.from({ length: 5 }).map((_, c) => (
                    <div key={c} className="sk" style={{ height: 12, flex: c === 2 ? 2 : 1, opacity: 1 - r * 0.1 }} />
                  ))}
                </div>
              ))}
            </div>
          ) : items.length === 0 ? (
            <div className="empty">
              <div className="empty-ico">
                <ReceiptText size={28} />
              </div>
              <div>
                <div className="t-h2" style={{ marginBottom: 4 }}>
                  Sin gastos
                </div>
                <div className="muted">Registra tus egresos para llevar el control.</div>
              </div>
              <button className="btn btn-primary" onClick={() => setDialog('expense')}>
                <Plus size={17} />
                Nuevo gasto
              </button>
            </div>
          ) : (
            <div className="tbl-wrap">
              <table className="tbl">
                <thead>
                  <tr>
                    <th>Fecha</th>
                    <th>Categoría</th>
                    <th>Concepto</th>
                    <th>Proveedor</th>
                    <th className="right">IGV</th>
                    <th className="right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((e) => (
                    <tr key={e.id}>
                      <td className="tnum">
                        <DateDisplay value={e.date} />
                      </td>
                      <td>
                        {e.category ? (
                          <span className="badge" style={{ background: `${catColor(e.category)}18`, color: catColor(e.category) }}>
                            <span className="bdot" />
                            {e.category}
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="strong">{e.description ?? '—'}</td>
                      <td className="muted">{e.supplierName ?? '—'}</td>
                      <td className="right tnum muted">
                        <Money value={e.igv ?? 0} />
                      </td>
                      <td className="right tnum strong">
                        <Money value={e.total} />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {dialog === 'expense' && <ExpenseDialog onClose={() => setDialog(null)} />}
      {dialog === 'category' && <CategoriesDialog onClose={() => setDialog(null)} />}
    </div>
  );
}

function ExpenseDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const create = useCreateExpense();
  const { data: categories } = useExpenseCategories();
  const { data: accounts } = useAccounts();
  const [f, setF] = useState({
    categoryId: '', supplierName: '', amount: '', igv: '', accountId: '', date: todayISO(), description: '',
  });
  const set = (k: keyof typeof f) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) =>
    setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    create.mutate(
      {
        categoryId: f.categoryId || undefined,
        supplierName: f.supplierName.trim() || undefined,
        amount: Number(f.amount),
        igv: Number(f.igv) || 0,
        accountId: f.accountId || undefined,
        date: f.date,
        description: f.description.trim() || undefined,
      },
      { onSuccess: () => onClose() },
    );
  };

  return (
    <CxDialog
      title="Nuevo gasto"
      sub="Registra un egreso de la empresa"
      icon={<ReceiptText size={20} />}
      iconTone="error"
      width={480}
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-secondary grow" onClick={onClose}>
            Cancelar
          </button>
          <button className="btn btn-primary grow" type="submit" form="exp-form" disabled={create.isPending || Number(f.amount) <= 0}>
            <Check size={18} />
            {create.isPending ? 'Guardando…' : 'Registrar gasto'}
          </button>
        </>
      }
    >
      <form id="exp-form" onSubmit={submit} className="grid" style={{ gridTemplateColumns: '1fr 1fr', gap: 14 }}>
        <div className="field">
          <label className="label">Categoría</label>
          <select className="select" value={f.categoryId} onChange={set('categoryId')}>
            <option value="">— Sin categoría —</option>
            {(categories ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="label">Fecha</label>
          <input className="input" type="date" value={f.date} onChange={set('date')} required />
        </div>
        <div className="field" style={{ gridColumn: 'span 2' }}>
          <label className="label">Concepto</label>
          <input className="input" value={f.description} placeholder="ej. Combustible, alquiler, servicios" onChange={set('description')} />
        </div>
        <div className="field">
          <label className="label">Proveedor</label>
          <input className="input" value={f.supplierName} placeholder="Opcional" onChange={set('supplierName')} />
        </div>
        <div className="field">
          <label className="label">Cuenta de pago</label>
          <select className="select" value={f.accountId} onChange={set('accountId')} disabled={(accounts?.length ?? 0) === 0}>
            <option value="">— No afectar tesorería —</option>
            {(accounts ?? []).map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label className="label">Monto</label>
          <input className="input tnum" type="number" step="any" min="0" value={f.amount} placeholder="0.00" required onChange={set('amount')} />
        </div>
        <div className="field">
          <label className="label">IGV</label>
          <input className="input tnum" type="number" step="any" min="0" value={f.igv} placeholder="0.00" onChange={set('igv')} />
        </div>
      </form>
    </CxDialog>
  );
}

function CategoriesDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const { data: categories } = useExpenseCategories();
  const create = useCreateCategory();
  const [name, setName] = useState('');
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (name.trim()) create.mutate(name.trim(), { onSuccess: () => setName('') });
  };
  return (
    <CxDialog title="Categorías de gasto" sub="Crea y administra tus categorías" icon={<Tags size={20} />} iconTone="primary" onClose={onClose}>
      <form onSubmit={submit} className="row gap8" style={{ marginBottom: 14 }}>
        <input className="input" value={name} placeholder="Nueva categoría" onChange={(e) => setName(e.target.value)} />
        <button type="submit" className="btn btn-primary" disabled={create.isPending || !name.trim()} style={{ flex: 'none' }}>
          <Plus size={16} />
          Agregar
        </button>
      </form>
      <div className="row wrap gap8">
        {(categories ?? []).map((c) => (
          <span key={c.id} className="badge badge-gray" style={{ padding: '6px 12px', fontSize: 13 }}>
            {c.name}
          </span>
        ))}
        {(categories ?? []).length === 0 && <div className="muted t-sm">Sin categorías aún.</div>}
      </div>
    </CxDialog>
  );
}
